import { FACET_UPDATE_DEFAULT_VERSION } from "../../tars-constants";
import type { UpdateFacetPayload } from "../../tars-hooks/useUpdateFacet";
import { safe_get } from "../../../../report-visuals";

export type FacetSnapshotForUpdate = {
  id: string;
  facet_id?: number;
  jsonContent?: string;
  etl_json?: Record<string, unknown>;
  facet_result?: Record<string, unknown>;
  more?: Record<string, unknown>;
  version?: number;
  [key: string]: unknown;
};

/** Prefer stored etl_json; otherwise parse jsonContent so updates match the editor. */
export function resolvedEtlJsonForPersist(
  facet: FacetSnapshotForUpdate
): Record<string, unknown> {
  const fromField = facet.etl_json;
  if (fromField && Object.keys(fromField).length > 0) {
    return fromField;
  }
  const raw = facet.jsonContent?.trim();
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed != null && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    /* ignore */
  }
  return {};
}

/** When ETL JSON includes `etl.name`, align facet list label with canvas rename (`name` + `more.name`). */
export function facetLabelUpdatesFromEtlParsed(
  facet: FacetSnapshotForUpdate,
  parsed: Record<string, unknown>
): { name: string; more: Record<string, unknown> } | null {
  const etl = safe_get(parsed, "etl", undefined);
  if (etl == null || typeof etl !== "object" || Array.isArray(etl)) return null;
  const raw = (etl as Record<string, unknown>).name;
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const prevMore = (facet.more as Record<string, unknown> | undefined) ?? {};
  return {
    name: trimmed,
    more: { ...prevMore, name: trimmed },
  };
}

export function resolveServerFacetId(facet: FacetSnapshotForUpdate): number | null {
  if (typeof facet.facet_id === "number" && Number.isFinite(facet.facet_id)) {
    return facet.facet_id;
  }
  const parsed = Number(facet.id);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function facetUpdateVersion(facet: FacetSnapshotForUpdate): number {
  const v = facet.version;
  return typeof v === "number" && Number.isFinite(v) ? v : FACET_UPDATE_DEFAULT_VERSION;
}

export function buildFacetUpdatePayload(
  facet: FacetSnapshotForUpdate,
  overrides: {
    etl_json: Record<string, unknown>;
    facet_result: Record<string, unknown>;
    more?: Record<string, unknown>;
  },
  notebookId?: number
): UpdateFacetPayload | null {
  const facet_id = resolveServerFacetId(facet);
  if (facet_id == null || facet_id <= 0) return null;
  return {
    facet_id,
    ...(notebookId != null && notebookId > 0 ? { notebook_id: notebookId } : {}),
    etl_json: overrides.etl_json,
    facet_result: overrides.facet_result,
    version: facetUpdateVersion(facet),
    more:
      overrides.more ??
      ((facet.more as Record<string, unknown> | undefined) ?? {}),
  };
}
